
import { RootState } from '@next/core-lib/app';
import { SessionState } from '@next/core-lib/session';

import { AdviceState } from '../advice/models';
import { RecommendedNutrientQuantityData } from '../advice/models/fertilization-result.model';
import { CopyAdviceState } from '../copy-advice/copy-advice.models';
import { CropAid } from '../crop-aid/crop-aid.model';
import { LegalRestriction } from '../legal-restriction/legal-restriction.model';
import { ReportSort } from '../reports/models';
import {
  SalesOrderPeriod,
  TreatmentType,
} from '../sales-order-period/models/sales-order-period.models';

// add your interfaces for the api responses and state here
// SiteState is the root state of your app module
export interface SiteState extends RootState {
  app?: AppState;
  advice?: AdviceState;
  copyAdvice?: CopyAdviceState;
}

export interface ArableSessionState extends SessionState {
  arable?: {
    cropYear?: number;
  };
}

export interface AppState {
  crops?: CropData[];
  nonProductiveCrops?: CropData[];
  cropFieldAdvices?: CropFieldAdvice[];
  defaultCropYear?: number;
  availableCropYears?: number[];
  loggedInEmployee?: Employee;
  skalReport?: SkalReport;
  planetProofReport?: PlanetProofReport;
  environmentalImpactReport?: EnvironmentalImpactReport;
  reportSort?: ReportSort;
  customerProgress?: CustomerProgress;
  companySettings?: CompanySettings;
  soilTypes?: SoilType[];
}

export interface CropVarietyData {
  id: number;
  name: string;
  ediCode?: number;
}

export interface SoilTypeData {
  id: number;
  name: string;
}

export interface CropFieldAdvice {
  cropFieldId: number;
  adviceId: number;
  cropYear: number;
  name: string;
  crop: { name: string; id: number };
  cropVariety: CropVarietyData;
  agrifirmCropId: number;
  soilType: SoilTypeData;
  isSkal: boolean;
  isPlanetProof: boolean;
  isGroundWaterPreservation: boolean;
  isSandySoil: boolean;
  isRentedField: boolean;
  isNutrientsPollutedArea: boolean;
  isGroundWaterPreservationWithNitrogenCuts: boolean;
  startDate: string;
  endDate: string;
  areaInHectares: number;
  province?: string;
  soilAnalysisIds: number[];
  treatments: Treatment[];
  gbmCompleted: boolean;
  fertilizationCompleted: boolean;
  gbmCorrupt: boolean;
  fertilizationCorrupt: boolean;
  syncStatus: string;
  syncSource: string;
  selected?: boolean;
}

export interface CropFieldAdviceDetail extends CropFieldAdvice {
  treatments: TreatmentDetail[];
}

export interface CropFieldAdviceWithLegalRestrictions extends CropFieldAdviceDetail {
  treatments: TreatmentWithLegalRestrictions[];
}

export interface CropFieldAdviceGroup {
  cropFieldAdvices: CropFieldAdviceWithLegalRestrictions[];
  cropId: number;
  cropName: string;
  cropVarietyName: string;
  totalHectares: number;
  treatments: TreatmentDetail[];
  compareBy: string;
}

export enum ReportType {
  GBMWithEnvironmentalImpactPoints = 'GBMWithEnvironmentalImpactPoints',
  Fertilization = 'Fertilization',
  FertilizationSplitFields = 'FertilizationSplitFields',
  LegalStandards = 'LegalStandards',
  Skal = 'Skal',
  FertilizationTableReport = 'FertilizationTableReport',
  OrganicMatter = 'OrganicMatter',
  NutrientBalance = 'NutrientBalance',
  EnvironmentalImpact = 'EnvironmentalImpact',
  PlanetProof = 'PlanetProof',
}

export enum WellKnownCropIds {
  GrassLandAgrifirmCropId = 1193,
}

export enum OrganicMatterSupply {
  Low = 'Low',
  Average = 'Average',
  High = 'High',
}

export interface TreatmentOrExecutedTreatment {
  id: string | number;
  aidAdvices: AidAdvice[];
  moment: string;
  name: string;
  objective: string;
  periodId: number;
  pointOfAttention: string;
  frequency: number;
  areaInHectaresManual: number;
  shouldUseAreaInHectaresManual: boolean;
  shouldUseCompanySettingsDriftReduction?: boolean;
  treatmentType: TreatmentType;
  driftReduction?: number;
}

export interface TreatmentOrExecutedTreatmentWithNitrogenEffectivity
  extends TreatmentOrExecutedTreatment {
  aidAdvices: AidAdviceWithNitrogenEffectivity[];
  isAnExecutedTreatment: boolean;
  adviceId?: number;
}

export interface Treatment extends TreatmentOrExecutedTreatment {
  id: string;
  isCorrupt?: boolean;
  forbiddenForPlanetProof?: boolean;
}

export interface TreatmentWithNitrogenEffectivity extends Treatment {
  aidAdvices: AidAdviceWithNitrogenEffectivity[];
  isAnExecutedTreatment: false;
}

export interface ExecutedTreatment extends TreatmentOrExecutedTreatment {
  id: number;
  adviceId: number;
  isAnExecutedTreatment?: boolean;
}

export interface ExecutedTreatmentWithNitrogenEffectivity extends ExecutedTreatment {
  aidAdvices: AidAdviceWithNitrogenEffectivity[];
  isAnExecutedTreatment: true;
}

export interface TreatmentDetail extends Treatment {
  aidAdvices: AidAdviceDetail[];
  period: SalesOrderPeriod;
}

export interface TreatmentWithLegalRestrictions extends TreatmentDetail {
  aidAdvices: AidAdviceDetailWithLegalRestriction[];
}

export interface AidAdvice {
  cropAidId: number;
  dosage: number;
  nitrogenEffectivity?: number;
}

export interface AidAdviceWithNitrogenEffectivity extends AidAdvice {
  shouldUseNitrogenEffectivityManual: boolean;
  nitrogenEffectivity: number;
}

export interface AidAdviceDetail extends AidAdvice {
  cropAid: CropAid;
}

export interface AidAdviceDetailWithLegalRestriction extends AidAdviceDetail {
  legalRestriction: LegalRestriction;
}

export interface CropAidAdvice extends AidAdvice, CropAid {}

export interface TreatmentSchema {
  name: string;
  description: string;
  treatmentIds: string[];
  isCorrupt?: boolean;
}

export interface Template {
  treatments: Treatment[];
  treatmentSchemas: TreatmentSchema[];
  isCorrupt?: boolean;
}

export interface Employee {
  firstName: string;
  lastName: string;
  emailAddress: string;
  mobilePhoneNumber: string;
}

export enum SeasonType {
  Spring = 'Spring',
  Autumn = 'Autumn',
}

export enum GrasslandUsage {
  Mow = 'Mow',
  Graze = 'Graze',
  NotGrassland = 'NotGrassland',
}

export interface CropData {
  id: number;
  agrifirmCrop: AgrifirmCrop;
  isPerennialCultivation: boolean;
  isStartDateInPreviousYear: boolean;
  isGreenManure: boolean;
  isLeguminous: boolean;
  defaultNitrogenFertilization: number;
  effectiveOrganicMatter: number;
  expectedCropYieldPerHa: number;
  nutritionHarvestedCrop: NutritionHarvestedCrop;
}

export interface AgrifirmCrop {
  id: number;
  name: string;
}

export interface NutritionHarvestedCrop {
  n: number;
  p2O5: number;
  k2O: number;
  mgO: number;
}

export interface GreenManureSetting {
  cropId: number;
  seasonGreenManureIncorporated: SeasonType;
  successRate: number;
  nitrogenDeliveryAmount: number;
  isNitrogenDeliveryAmountSetManually: boolean;
}

export interface PrecedingCropSetting {
  cropId: number;
  nitrogenDeliveryAmount: number;
  isNitrogenDeliveryAmountSetManually: boolean;
}

export interface NitrogenCalculationSettings {
  isPreviousCropCorn: boolean;
  isPreviousCropNotGrain: boolean;
  grasslandUsage: GrasslandUsage;
  shouldSplitNorm: boolean;
  isNitrogenDifferentiation: boolean;
  isAfterTornGrass: boolean;
  isRecoveryFertilisation: boolean;
}

export interface NitrogenCalculationSettingToggles {
  adviceId: number;
  shouldShowPreviousCropCornToggle: boolean;
  shouldShowPreviousCropGrainToggle: boolean;
  shouldShowGrasslandUsage: boolean;
  shouldShowShouldSplitNormToggle: boolean;
  shouldShowNitrogenDifferentiationToggle: boolean;
  shouldShowAfterTornGrassToggle: boolean;
  shouldShowOrganicMatterToggle: boolean;
}

export interface FertilizationBalanceSettings {
  samplesByFertilab: boolean;
  samplesByFarmer: boolean;
}

export interface FertilizationSetting {
  isAcceptedByUser: boolean;
  isPrecedingCrop: boolean;
  isGreenManure: boolean;
  shouldBeUsedInUsageStandard: boolean;
  shouldBeUsedInLegalStandard: boolean;
  shouldUseTransitionalArrangement: boolean;
  shouldUseLowInPhosphateArrangement: boolean;
  greenManureSettings?: GreenManureSetting;
  precedingCropSettings?: PrecedingCropSetting;
  nitrogenCalculationSettings?: NitrogenCalculationSettings;
  organicMatterSupply: OrganicMatterSupply;
  expectedCropYield: number;
}

export interface FertilizationSettingWithIds extends FertilizationSetting {
  id: number;
  adviceId: number;
  customerId: number;
}

export interface FertilizationSettingWithName extends FertilizationSettingWithIds {
  cropAdviceName: string;
  cropName: string;
  cropId: number;
}

export interface UpsertFertilizationSettingRequest extends FertilizationSetting {
  cropYear: number;
  adviceId: number;
}

export interface NitrogenDeliveryRate {
  isLeguminous: boolean;
  successRate: number;
  autumnNitrogenDeliveryAmount: number;
  springNitrogenDeliveryAmount: number;
}

export interface Objective {
  name: string;
  value: string;
}

export interface CropAndCropAid {
  cropId: number;
  cropAidId: number;
}

export interface SkalReport {
  id: number;
  customerId: number;
  cropYear: number;
  totalArea: number;
  totalSkalA: number;
  totalSkalB: number;
  totalNitrogen: number;
  percentageSkalA: number;
  percentageSkalB: number;
  cropFields: SkalReportCropField[];
}

export interface SkalReportCropField {
  cropFieldId: number;
  name: string;
  crop: string;
  areaInHectares: number;
  skalItems: SkalReportCropFieldItem[];
}

export interface SkalReportCropFieldItem {
  cropAidId: number;
  skalANitrogen: number;
  skalBNitrogen: number;
}

export interface FertilizationTableReportAdvice {
  adviceId: number;
  cropFieldNutrientRequisites: RecommendedNutrientQuantityData;
  fertilizationSettings: FertilizationSetting;
  executedTreatments: ExecutedTreatmentWithNitrogenEffectivity[];
}

export interface TargetValues {
  minimum: number;
  maximum: number;
  evaluation: number;
}

export interface SoilAnalysis {
  id: number;
  customerId: number;
  isManualEntry: boolean;
  description: string;
  samplingDateTime: string;
  sampledCharacteristics: Characteristic[];
  isDefault: boolean;
}

export interface Characteristic {
  id: number;
  unitOfMeasurement: string;
  value: number;
  designation: string;
  name: string;
  targetValues: TargetValues;
}

export interface OrganicMatterReport {
  totals: OrganicMatterBalanceTotals;
  cropFields: OrganicMatterBalanceCropField[];
}

export interface OrganicMatterBalanceTotals {
  hectares: number;
  cropOrganicMatterSupply: number;
  cropOrganicMatterSupplyPerHectare: number;
  fertilizersOrganicMatterSupply: number;
  fertilizersOrganicMatterSupplyPerHectare: number;
  cropFieldOrganicMatterBreakdown: number;
  cropFieldOrganicMatterBreakdownPerHectare: number;
  organicMatterBalance: number;
  organicMatterBalancePerHectare: number;
}

export interface OrganicMatterBalanceCropField extends OrganicMatterBalanceTotals {
  name: string;
  cropName: string;
  cropFieldId: number;
}

export interface OrganicMatterTable {
  name: string;
  translateName: boolean;
  totalHectares: number;
  balance: number;
  balancePerHectare: number;
  organicMatterItems: OrganicMatterItem[];
  metaData: { cropFieldName?: string; cropName?: string; isTotals?: boolean };
}

export interface OrganicMatterItem {
  source: string;
  data: OrganicMatterData;
}

export interface OrganicMatterData {
  total: number;
  perHectare: number;
}

export interface NutrientBalanceTable {
  name: string;
  translateName: boolean;
  totalHectares: number;
  nutrientBalanceItems: NutrientBalanceItem[];
  metaData: {
    cropFieldName?: string;
    cropName?: string;
    cropVarietyName?: string;
    isTotals?: boolean;
  };
}

export interface NutrientBalanceItem {
  source: string;
  data: NutrientBalanceData;
}

export interface NutrientBalanceData {
  supply: number;
  drainage: number;
  balance: number;
}

export interface ActiveIngredient {
  ingredientId?: number | null;
  name: string;
  kgAsPerHA: number | null;
  bonus: number;
  malus: number;
  admissionNumber: string;
  forbidden?: boolean;
}

export interface EnvironmentalImpactReport {
  cropFields: CropFieldImpact[];
}

export interface CropFieldImpact {
  cropFieldName: string;
  cropName: string;
  cropFieldId: number;
  cropVarietyName: string;
  cropFieldArea: number;
  organicMatterPercentage?: number;
  cropAids: CropAidImpact[];
  totals: CropAidImpactTotals;
}

export interface CropAidImpact {
  cropAidName: string;
  admissionNumber: string;
  dosage: number;
  unitOfMeasurement: string;
  activeIngredientAmount: number;
  driftReduction: number;
  aquaticLife?: number;
  soilLife?: number;
  groundwaterWashout?: number;
  pollinators: string;
  combatants: string;
}

export interface CropAidImpactTotals {
  activeIngredientAmount: number;
  aquaticLife: number;
  soilLife: number;
  groundwaterWashout: number;
}

export interface PlanetProofReportCropAid {
  id?: number;
  name: string;
  type: string;
  dosage: number;
  unitOfMeasurement: string;
  glyphosateUsage: number;
  activeIngredients: ActiveIngredient[];
  bonus: number;
}

export interface PlanetProofReportCropField {
  customerId?: number;
  cropFieldId: number;
  adviceId?: number;
  cropYear?: number;
  cropFieldName: string;
  cropId: number;
  cropName: string;
  areaInHectares: number;
  cultivationMonths: number;
  cropAids: PlanetProofReportCropAid[];
  totalActiveMatter?: number;
  totalBonus?: number;
  totalMalus?: number;
  saldo?: number;
}

export interface PlanetProofCrop {
  cropId: number;
  cropName: string;
  areaInHectares: number;
  activeMatter: number;
  cropNorm: number | null;
  bonus: number;
  malus: number;
  extraBonus: number;
  saldo: number;
  ingredients?: PlanetProofIngredient[];
}

export interface PlanetProofIngredient {
  ingredientId?: number | null;
  name: string;
  activeMatter: number | null;
  malus: number;
  forbidden: boolean;
}

export interface PlanetProofReport {
  // Optional only for the legacy endpoint and during a mixed-version rollout.
  calculationStatus?: 'complete' | 'incomplete' | 'notCalculated';
  calculationIssues?: string[];
  cropFields: PlanetProofReportCropField[];
  crops: PlanetProofCrop[];
  totalHectares: number;
  totalGlyphosateUsage: number;
  glyphosateMalusPoints: number;
}

export interface GetDefaultYearResponse {
  defaultYear: number;
}

export interface CustomerProgress {
  id: number;
  customerId: number;
  year: number;
  processes: CustomerProgressProcesses;
}

export interface CustomerProgressProcesses {
  fieldPlanCropFieldsCompleted: boolean;
  fieldPlanManurespaceCompleted: boolean;
  fieldAdviceFertilizationAdvicesCompleted: boolean;
  fieldAdviceCropProtectionAdvicesCompleted: boolean;
  orderplanningOrderCompleted: boolean;
  manurespaceCalculationsCompleted: boolean;
}

export const customerProgressForSelectionUrl =
  '/arable-service/organisation/customerprogress/customers';

export interface OrderPlanningCustomerPreference {
  customerId: number;
  shipToId: number;
  year: number;
  preferedDeliveryPeriods: number[];
  periodSystem: string;
}

export interface OrderPlanningCustomerPreferenceResponse {
  customerId: number;
  year: number;
  preferences: OrderPlanningCustomerPreference[];
}

export interface CompanySettings {
  customerId: number;
  driftReduction: number;
  grasslandUsage: GrasslandUsage;
  yearlySettings: YearlySettings;
  defaultSoilTypeId: number;
  automaticAdvice: boolean;
}

export interface UpsertCompanySettingsRequest {
  driftReduction: number;
  yearlySettings: YearlySettings;
  defaultSoilTypeId: number;
  automaticAdvice: boolean;
}

export interface YearlySettings {
  nitrogenCuts: NitrogenCuts;
  year: number;
  showReminderForGrainRegistration: boolean;
}

export interface NitrogenCuts {
  cutsInHaPeriod1: number;
  cutsInHaPeriod2: number;
  cutsInHaPeriod3: number;
}

export interface UpdateGrasslandUsageRequest {
  year: number;
  grasslandUsage: GrasslandUsage;
}

export interface CustomerFertilizerStock {
  cropYear: number;
  customerId: number;
  fertilizerStock: CustomerFertilizerStockRow[];
}

export interface CustomerFertilizerStockRow {
  cropAidId: number;
  stock: number;
}

export interface PhosphateLegalWorkingCoefficient {
  id: number;
  calendarYear: number;
  phosphateWorkingCoefficient: number;
  cropAidId: number;
}

export interface SoilType {
  id: number;
  name: string;
}

export enum SamplesBy {
  Fertilab = 'Fertilab',
  Farmer = 'Farmer'
}

export interface FertilizationBalanceSetting {
  id: string;
  adviceId: number;
  name: string;
  areaInHectares: number;
  cropName: string;
  areSampleDatesConfirmed?: boolean;
  isParticipating: boolean;
  isCanceling: boolean;
  samplesBy: SamplesBy;
  expectedCropYieldProduct: number;
  participationMailDate?: string;
  cancelationDate?: string;
  externalOrderId?: string;
  orderDate?: string;
  customerPlantDate?: Date;
  ediVarietyCode: number;
  cropVarietyName: string;
  businessRuleViolationTranslationCodes: string[];
  specificReferenceBlockData?: SpecificReferenceBlock;
  labResultReceiveDates?: LabResultReceiveDates;
  isPlantDateConfirmed?: boolean;
}

export interface SpecificReferenceBlock {
  longitude: number;
  latitude: number;
  remarks: string;
}
export interface LabResultReceiveDates {
  n1ReceivedDateUtcB: Date,
  n2ReceivedDateUtcP: Date,
  n3ReceivedDateUtcB: Date,
  n3ReceivedDateUtcP: Date
}

export interface FertilizationBalanceSettingsResponse {
  fertilizationBalanceSettings: FertilizationBalanceSetting[];
  headerBusinessRuleViolations: string[];
}
export interface ArableAdviceValidationErrorResponse {
  message: string;
  statusCode: number;
  validationMessageCodes: string[];
}
